import { z } from 'zod';
import type { StartUfpInventoryRunInput } from '~/graphql/graphql';

export type RunRequestForm = {
  branchCodes: string;
  weeksToLookBack: string;
  apiVersion: string;
};

export type RunRequestErrors = Partial<Record<keyof RunRequestForm, string>>;

export const DEFAULT_RUN_REQUEST: RunRequestForm = {
  branchCodes: '',
  weeksToLookBack: '4',
  apiVersion: '1',
};

export const RUN_REQUEST_LIMITS = {
  maxBranchCodes: 5,
  maxWeeksToLookBack: 52,
  maxApiVersion: 99,
} as const;
const WHOLE_NUMBER = /^\d+$/;

const boundedWholeNumber = (max: number, message: string) =>
  z
    .string()
    .regex(WHOLE_NUMBER, message)
    .transform(Number)
    .pipe(z.number().min(1, message).max(max, message));

const runRequestSchema = z.object({
  branchCodes: z
    .array(
      z.string().regex(/^[A-Za-z0-9]{1,10}$/, 'form.errors.invalidBranchCode')
    )
    .min(1, 'form.errors.branchCodesRequired')
    .max(RUN_REQUEST_LIMITS.maxBranchCodes, 'form.errors.tooManyBranchCodes'),
  weeksToLookBack: boundedWholeNumber(
    RUN_REQUEST_LIMITS.maxWeeksToLookBack,
    'form.errors.invalidWeeksToLookBack'
  ),
  apiVersion: boundedWholeNumber(
    RUN_REQUEST_LIMITS.maxApiVersion,
    'form.errors.invalidApiVersion'
  ),
});

export function parseBranchCodes(text: string): string[] {
  return [...new Set(text.split(/[\s,]+/).filter((code) => code !== ''))];
}

const isFormField = (key: PropertyKey): key is keyof RunRequestForm =>
  key in DEFAULT_RUN_REQUEST;

export function validateRunRequest(form: RunRequestForm): {
  input: StartUfpInventoryRunInput | null;
  errors: RunRequestErrors;
} {
  const parsed = runRequestSchema.safeParse({
    ...form,
    branchCodes: parseBranchCodes(form.branchCodes),
  });
  if (parsed.success) {
    return { input: parsed.data, errors: {} };
  }

  const errors: RunRequestErrors = {};
  for (const issue of parsed.error.issues) {
    const field = issue.path[0];
    if (isFormField(field)) {
      errors[field] ??= issue.message;
    }
  }
  return { input: null, errors };
}
